import {
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Typography,
  CircularProgress,
  Pagination,
  IconButton,
  Card,
  CardContent,
  Chip,
  Avatar,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Close,
  AccountBalanceWallet,
  TrendingUp,
  TrendingDown,
  Receipt,
  CalendarToday,
  BookOutlined,
} from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../../firebase";

function WalletList({ publisherId }) {
  const toggleDrawer = (newOpen) => () => {
    setOpenWalletDrawer(newOpen);
  };
  const [openWalletDrawer, setOpenWalletDrawer] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [lastVisibleByPage, setLastVisibleByPage] = useState({});
  const transactionsPerPage = 5;

  useEffect(() => {
    const fetchTransactions = async () => {
      if (openWalletDrawer && publisherId) {
        setLoading(true);
        try {
          const transactionsRef = collection(
            db,
            `/publishers/${publisherId}/transactions`
          );

          // Get total count for pagination (only once when drawer opens)
          if (page === 1 || Object.keys(lastVisibleByPage).length === 0) {
            const countSnapshot = await getCountFromServer(
              collection(db, `/publishers/${publisherId}/transactions`)
            );
            setTotalPages(
              Math.ceil(countSnapshot.data().count / transactionsPerPage)
            );
          }

          let q;
          if (page === 1) {
            q = query(
              transactionsRef,
              orderBy("timestamp", "desc"),
              limit(transactionsPerPage)
            );
          } else if (lastVisibleByPage[page - 1]) {
            q = query(
              transactionsRef,
              orderBy("timestamp", "desc"),
              startAfter(lastVisibleByPage[page - 1]),
              limit(transactionsPerPage)
            );
          } else {
            // If we don't have the previous page's last document, we can't fetch this page
            setLoading(false);
            return;
          }

          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const transactionsData = querySnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            setLastVisibleByPage((prev) => ({
              ...prev,
              [page]: querySnapshot.docs[querySnapshot.docs.length - 1],
            }));

            setTransactions(transactionsData);
          } else {
            setTransactions([]);
          }
        } catch (error) {
          console.error("Error fetching transactions:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchTransactions();
  }, [openWalletDrawer, publisherId, page]);

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const theme = useTheme();

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate();
    return `on ${date.getDate()}${getOrdinalSuffix(
      date.getDate()
    )} ${date.toLocaleString("default", {
      month: "long",
    })} ${date.getFullYear()}, ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<AccountBalanceWallet />}
        onClick={() => {
          setOpenWalletDrawer(true);
        }}
        sx={{
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 500,
          px: 3,
          py: 1,
        }}
      >
        View Transactions
      </Button>
      <Drawer
        open={openWalletDrawer}
        anchor={"right"}
        onClose={toggleDrawer(false)}
        sx={{
          zIndex: 1400,
          "& .MuiDrawer-paper": {
            marginTop: "64px",
            height: "calc(100vh - 64px)",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.02
            )} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
            backdropFilter: "blur(10px)",
            borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            "@media (max-width: 768px)": {
              marginTop: "56px",
              height: "calc(100vh - 56px)",
              width: "100vw",
            },
          },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: 500 },
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              pb: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              background: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(10px)",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    width: 48,
                    height: 48,
                  }}
                >
                  <AccountBalanceWallet />
                </Avatar>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    color="text.primary"
                  >
                    Transaction History
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your wallet activity
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={toggleDrawer(false)}
                sx={{
                  bgcolor: alpha(theme.palette.action.hover, 0.5),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.action.hover, 0.8),
                  },
                }}
              >
                <Close />
              </IconButton>
            </Stack>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3,
              pt: 2,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 300,
                  gap: 2,
                }}
              >
                <CircularProgress size={48} />
                <Typography variant="body2" color="text.secondary">
                  Loading transactions...
                </Typography>
              </Box>
            ) : transactions.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 300,
                  gap: 2,
                  textAlign: "center",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.action.hover, 0.5),
                    width: 64,
                    height: 64,
                    mb: 1,
                  }}
                >
                  <Receipt sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" color="text.primary">
                  No transactions yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your transaction history will appear here
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {transactions.map((transaction, index) => (
                  <Card
                    key={transaction.id}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: `0 2px 8px ${alpha(
                        theme.palette.common.black,
                        0.04
                      )}`,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 8px 24px ${alpha(
                          theme.palette.common.black,
                          0.12
                        )}`,
                      },
                      animation: `slideIn 0.3s ease-out ${index * 0.1}s both`,
                      "@keyframes slideIn": {
                        from: {
                          opacity: 0,
                          transform: "translateX(20px)",
                        },
                        to: {
                          opacity: 1,
                          transform: "translateX(0)",
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        {/* Transaction Header */}
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            flex={1}
                          >
                            <Avatar
                              sx={{
                                bgcolor:
                                  transaction.type === "credit"
                                    ? alpha(theme.palette.success.main, 0.1)
                                    : alpha(theme.palette.error.main, 0.1),
                                color:
                                  transaction.type === "credit"
                                    ? theme.palette.success.main
                                    : theme.palette.error.main,
                                width: 40,
                                height: 40,
                              }}
                            >
                              {transaction.type === "credit" ? (
                                <TrendingUp />
                              ) : (
                                <TrendingDown />
                              )}
                            </Avatar>
                            <Box flex={1}>
                              <Typography
                                variant="subtitle1"
                                fontWeight={500}
                                color="text.primary"
                              >
                                {transaction.message}
                              </Typography>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                mt={0.5}
                              >
                                <CalendarToday
                                  sx={{ fontSize: 14, color: "text.secondary" }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatDate(transaction.timestamp)}
                                </Typography>
                              </Stack>
                            </Box>
                          </Stack>

                          <Chip
                            label={`${
                              transaction.type === "credit" ? "+" : "-"
                            }₹${transaction.amount}`}
                            color={
                              transaction.type === "credit"
                                ? "success"
                                : "error"
                            }
                            variant="filled"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              minWidth: 80,
                              "& .MuiChip-label": {
                                px: 2,
                              },
                            }}
                          />
                        </Stack>

                        {/* Transaction Details */}
                        {transaction.bookId && (
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.success.main, 0.05),
                              borderRadius: 2,
                              border: `1px solid ${alpha(
                                theme.palette.success.main,
                                0.1
                              )}`,
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="flex-start"
                              spacing={1.5}
                            >
                              <BookOutlined
                                sx={{
                                  fontSize: 18,
                                  color: theme.palette.success.main,
                                  mt: 0.2,
                                }}
                              />
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.primary"
                                  fontWeight={500}
                                >
                                  Book Sale Commission
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  Book ID: {transaction.bookId} • Sale Price: ₹
                                  {transaction.bookPrice} • Commission:{" "}
                                  {transaction.commissionRate}%
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        )}

                        {transaction.title && !transaction.bookId && (
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.info.main, 0.05),
                              borderRadius: 2,
                              border: `1px solid ${alpha(
                                theme.palette.info.main,
                                0.1
                              )}`,
                            }}
                          >
                            <Typography variant="body2" color="text.primary">
                              {transaction.title}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>

          {/* Footer with Pagination */}
          {!loading && transactions.length > 0 && totalPages > 1 && (
            <Box
              sx={{
                p: 3,
                pt: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(10px)",
              }}
            >
              <Stack direction="row" justifyContent="center">
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Stack>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
}
export default WalletList;
